import { SEO } from '../components/SEO';
import { companyInfo } from '../data/content';
import { Target, Heart, Eye, Sparkles, Shield, Clock } from 'lucide-react';

export function UeberUns() {
  return (
    <>
      <SEO 
        title="Über uns"
        description="Lernen Sie ConciergeUnit kennen. Gegründet von Belbachir Soufiane – Ihr Partner für professionelle Empfangsdienstleistungen in Düsseldorf."
        path="/ueber-uns"
      />

      {/* Header Section */}
      <section className="pt-32 pb-20 bg-brand-950">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="text-platin-400 font-semibold text-sm uppercase tracking-widest">
              Über uns
            </span>
            <h1 className="heading-xl text-white mt-4 mb-6">
              Ihr vertrauensvoller Partner in der Hotellerie
            </h1>
            <p className="text-xl text-platin-200 leading-relaxed">
              ConciergeUnit steht für Professionalität, Zuverlässigkeit und erstklassigen Service. 
              Gegründet mit der Vision, Hotels und Veranstalter mit flexiblen, 
              qualifizierten Fachkräften zu unterstützen.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-brand-600 font-semibold text-sm uppercase tracking-widest">
                Unsere Geschichte
              </span>
              <h2 className="heading-md text-brand-950 mt-3 mb-6">
                Von der Vision zur Realität
              </h2>
              <div className="space-y-4 text-body">
                <p>
                  ConciergeUnit wurde von <strong>{companyInfo.owner}</strong> gegründet, 
                  um eine Lücke im Markt der Hotel-Dienstleistungen zu schließen. 
                  Nach jahrelanger Erfahrung in verschiedenen 4- und 5-Sterne-Hotels 
                  erkannte er die wachsende Nachfrage nach qualifiziertem, flexiblen Personal.
                </p>
                <p>
                  Was als kleiner Service begann, hat sich zu einer festen Größe in der 
                  Branche entwickelt. Heute unterstützen wir Hotels und Veranstalter 
                  deutschlandweit mit professionellem Personal für Night-Audit, 
                  Rezeption und Events.
                </p>
                <p>
                  Unser Erfolg basiert auf drei Säulen: <strong>Professionalität</strong>, 
                  {' '}<strong>Zuverlässigkeit</strong> und <strong>Flexibilität</strong>. 
                  Wir verstehen die Bedürfnisse unserer Kunden und liefern maßgeschneiderte 
                  Lösungen, die überzeugen.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-[4/3] bg-gradient-to-br from-brand-100 to-platin-100 rounded-2xl overflow-hidden shadow-luxury">
                <img
                  src="/images/image.png"
                  alt="Belbachir Soufiane - Geschäftsführer von ConciergeUnit"
                  className="w-full h-full object-cover"
                  width="800"
                  height="600"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.parentElement!.innerHTML = `
                      <div class="w-full h-full flex items-center justify-center bg-brand-900">
                        <div class="text-center text-platin-200 p-8">
                          <div class="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg class="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                          </div>
                          <p class="text-2xl font-bold mb-2">ConciergeUnit</p>
                          <p class="text-platin-400">Excellence in Hospitality</p>
                        </div>
                      </div>
                    `;
                  }}
                />
              </div>
              {/* Stats Card */}
              <div className="absolute -bottom-8 -left-8 bg-white rounded-xl p-6 shadow-luxury">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-brand-950 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-7 h-7 text-platin-200" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-brand-950">10+</p>
                    <p className="text-gray-600 text-sm">Jahre Erfahrung</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-platin-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-brand-600 font-semibold text-sm uppercase tracking-widest">
              Unsere Werte
            </span>
            <h2 className="heading-md text-brand-950 mt-3 mb-4">
              Was uns auszeichnet
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card-luxury p-8 group">
              <div className="w-16 h-16 bg-brand-950 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-platin-200 transition-colors duration-300">
                <Target className="w-8 h-8 text-platin-200 group-hover:text-brand-950 transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-brand-950 mb-3">Professionalität</h3>
              <p className="text-gray-600">
                Wir liefern stets höchste Qualität. Jedes Teammitglied wird sorgfältig 
                ausgewählt und auf die Anforderungen unserer Kunden vorbereitet.
              </p>
            </div>

            <div className="card-luxury p-8 group">
              <div className="w-16 h-16 bg-brand-950 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-platin-200 transition-colors duration-300">
                <Heart className="w-8 h-8 text-platin-200 group-hover:text-brand-950 transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-brand-950 mb-3">Leidenschaft</h3>
              <p className="text-gray-600">
                Hotellerie ist nicht nur ein Job, sondern eine Berufung. Unsere Leidenschaft 
                für den Service spiegelt sich in jedem Detail wider.
              </p>
            </div>

            <div className="card-luxury p-8 group">
              <div className="w-16 h-16 bg-brand-950 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-platin-200 transition-colors duration-300">
                <Eye className="w-8 h-8 text-platin-200 group-hover:text-brand-950 transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-brand-950 mb-3">Transparenz</h3>
              <p className="text-gray-600">
                Ehrliche Kommunikation und klare Absprachen sind die Grundlage 
                unserer erfolgreichen Zusammenarbeit mit Kunden und Mitarbeitern.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section - replaces Team Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-brand-600 font-semibold text-sm uppercase tracking-widest">
              Unsere Vision
            </span>
            <h2 className="heading-md text-brand-950 mt-3 mb-4">
              Wo wir hinwollen
            </h2>
            <p className="text-gray-600">
              Wir möchten die Hotellerie-Branche durch flexible, qualifizierte Dienstleistungen 
              bereichern und gleichzeitig talentierten Fachkräften attraktive Arbeitsmöglichkeiten bieten.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-platin-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-brand-600" />
              </div>
              <h3 className="font-bold text-brand-950 mb-2">Qualität</h3>
              <p className="text-gray-600 text-sm">Höchste Standards in jeder Dienstleistung</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-platin-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-brand-600" />
              </div>
              <h3 className="font-bold text-brand-950 mb-2">Verfügbarkeit</h3>
              <p className="text-gray-600 text-sm">Rund um die Uhr für Sie da</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-platin-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-brand-600" />
              </div>
              <h3 className="font-bold text-brand-950 mb-2">Innovation</h3>
              <p className="text-gray-600 text-sm">Immer am Puls der Zeit</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-platin-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-brand-600" />
              </div>
              <h3 className="font-bold text-brand-950 mb-2">Fairness</h3>
              <p className="text-gray-600 text-sm">Faire Partnerschaften auf Augenhöhe</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="section-padding bg-brand-950">
        <div className="container-custom text-center">
          <h2 className="heading-md text-white mb-6">
            Unsere Mission
          </h2>
          <p className="text-xl text-platin-200 max-w-3xl mx-auto leading-relaxed">
            Qualität, Fairness und nachhaltige Partnerschaften stehen im Mittelpunkt unseres Handelns. 
            Wir schaffen Brücken zwischen erstklassigen Hotels und qualifizierten Fachkräften – 
            für eine Hotellerie, die begeistert.
          </p>
        </div>
      </section>
    </>
  );
}
